import i18next from 'i18next';
import { BaseUITools } from "@/uiControls/base/baseUITools";
import { FileController } from "@/visionaryControllers/fileController";
import { ModelViewController } from "@/visionaryControllers/modelViewController";
import { VisionaryScene, VisionaryObject } from "@/visionaryControllers/visionaryScene";
import { ListItemOption, SelectionListUI } from "../base/ui/selectionListUI";
import { globalConfig, globalManager, modelDialogTools } from "@/utils/globalConfig";
import { getI18nText } from "@/utils/commonFunc";

declare const bootstrap: any;
import * as THREE from 'three';
import { RecordingCamera } from 'visionary-core';

interface MarbleModelPanelItem {
    worldid: string;
    status: string;
    modelUrl: string;
    name: string;
}

/**
 * 模型视图面板，负责管理模型列表，支持模型导入、删除、选择、显示、隐藏等操作
 * 导入模型的具体功能可参考model-tools.ts
 */
export class ModelViewPanelTools extends BaseUITools {
    private isExpanded: boolean = false; // 是否展开
    private currentSelectedId: string = '';
    private fileController!: FileController;
    private modelViewController!: ModelViewController;
    private modelListId: string;
    private modelListElement!: SelectionListUI;
    private modelListContainer!: HTMLElement;
    private cameraListElementId: string;
    private cameraListElement!: SelectionListUI;
    private cameraListContainer!: HTMLElement;
    private readonly allowedDragExtensions: string[] = ['onnx', 'ply', 'glb', 'obj', 'fbx', 'stl', 'spz'];
    private dragHandlers: {
        dragenter: (event: DragEvent) => void;
        dragover: (event: DragEvent) => void;
        dragleave: (event: DragEvent) => void;
        drop: (event: DragEvent) => void;
        dragend: (event: DragEvent) => void;
    } | null = null;
    private dragCounter: number = 0;
    private onModelSelectCallbacks: ((id: string, sceneid: string) => void)[] = [];
    private modelSelectChangeHandler!: (event: Event) => void;
    private clearObjectSelectHandler!: (event: Event) => void;
    private marbleModelResponseHandler!: (event: Event) => void;
    private panelId: string;
    private visionaryScene: VisionaryScene;
    private camerasCount: number = 0;
    private marbleModelList: MarbleModelPanelItem[] = [];

    constructor(id: string, scene: VisionaryScene) {
        super(id, false);
        this.panelId = id;
        this.cameraListElementId = `${id}_camera_list`;
        this.modelListId = `${id}_model_list`;
        this.visionaryScene = scene;
        this.fileController = new FileController(scene);
        this.modelViewController = new ModelViewController(scene);
        this.init();
        this.setupModelViewEventHandlers();
    }
    init() {
        const panelContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-container',
        });
        this.appendChild('panelContainer', panelContainerElement);

        const titleElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title',
        });
        const titleLeftElement = this.ui.createElement({
            tag: 'div',
            textContent: '场景管理',
            className: 'panel-setting-title-left',
            i18nKey: 'title.modelviewSettings',
        });
        const titleRightElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title-right',
        });

        const expandButtonElement = this.ui.createButton({
            iconName: 'ExpandOutlined',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'expand-button'],
            // i18nKey: 'buttons.close'
        })
        expandButtonElement.addEventListener('click', () => {
            this.toogleExpand();
        });
        titleRightElement.appendChild(expandButtonElement);
        titleElement.appendChild(titleLeftElement);
        titleElement.appendChild(titleRightElement);
        panelContainerElement.appendChild(titleElement);

        const cameraListElement = new SelectionListUI({
            id: this.cameraListElementId,
            label: '相机列表',
            showLabel: true,
            showAddBtn: true,
            showEmpty: false,
            isShowHoverBtn: true,
            containerClass: 'vision-model-list-container',
            selectItemList: [],
            i18nKey: 'label.cameraList',
        });

        this.cameraListElement = cameraListElement;

        const cameraListContainer = cameraListElement.getContainer();
        cameraListContainer.classList.add('camera-list-wrapper');
        this.cameraListContainer = cameraListContainer;
        panelContainerElement.appendChild(cameraListContainer);

        const modelListElement = new SelectionListUI({
            id: this.modelListId,
            label: '模型列表',
            showLabel: true,
            showAddBtn: true,
            showEmpty: true,
            isShowHoverBtn: true,
            containerClass: 'vision-model-list-container',
            selectItemList: [],
            i18nKey: 'label.modelList',
        });

        this.modelListElement = modelListElement;

        const modelListContainer = modelListElement.getContainer();
        // modelListContainer.classList.add('model-list-wrapper');
        this.modelListContainer = modelListContainer;
        panelContainerElement.appendChild(modelListContainer);
        this.appendChild('panelContainer', panelContainerElement);
    }
    private cameraItemOnDelete(option: ListItemOption) {
        const name = option.text || option.value;
        const deleteModelTitle = getI18nText('message.deleteCamera.title');
        const deleteModelConfirmBefore = getI18nText('message.deleteCamera.confirmBefore');
        const deleteModelConfirmAfter = getI18nText('message.deleteCamera.confirmAfter');
        modelDialogTools.showConfirmDialog(
            deleteModelTitle,
            `${deleteModelConfirmBefore} "${name}" ${deleteModelConfirmAfter}`,
            () => {
                if (this.currentSelectedId === option.value) {
                    const selectedObject = this.visionaryScene.getObjectById(option.value);
                    if (selectedObject && selectedObject.type === 'recordingCamera') { // 删除录制相机时，隐藏录制相机预览
                        const sceneObject = selectedObject.object;
                        const recordingCamera = sceneObject.userData.recordingCamera;
                        if (recordingCamera && typeof recordingCamera.hidePreviewWindow === 'function') {
                            recordingCamera.hidePreviewWindow();
                        }
                    }
                }
                this.modelViewController.doObjectDelete(option.value);
                this.updateModelList();
            },
            () => {
                // console.log('modelview panel tools delete canceled', option);
            }
        );
    }
    private cameraItemOnTextClick(option: ListItemOption) {
        this.currentSelectedId = option.value;
        this.modelViewController.doObjectSelect(option.value);
        this.notifyModelSelect(option.value);

        // 检查是否是录制相机，如果是则显示预览
        const selectedObject = this.visionaryScene.getObjectById(option.value);
        if (selectedObject && selectedObject.type === 'recordingCamera') { // 如果选中的是录制相机，打开预览；如果不是录制相机，保持录制相机预览显示
            const sceneObject = selectedObject.object;
            if (sceneObject.userData && sceneObject.userData.type === 'recordingCamera') {
                const recordingCamera = sceneObject.userData.recordingCamera;
                if (recordingCamera && typeof recordingCamera.ensurePreviewWindow === 'function') {
                    recordingCamera.ensurePreviewWindow(i18next.language as string);
                    // 使用场景的公共方法显示预览

                    recordingCamera.showPreviewWindow(globalConfig.lightSettings.skyboxSettings.enabled);
                    setTimeout(() => {
                        this.visionaryScene.showRecordingCameraPreview(recordingCamera).catch(error => {
                            console.error('[ModelViewPanelTools] 显示录制相机预览失败:', error);
                        });
                    }, 200);
                }
            }
        }

        this.updateModelList();
    }
    private modelItemOnDelete(option: ListItemOption) {
        const name = option.text || option.value;
        const deleteModelTitle = getI18nText('message.deleteModel.title');
        const deleteModelConfirmBefore = getI18nText('message.deleteModel.confirmBefore');
        const deleteModelConfirmAfter = getI18nText('message.deleteModel.confirmAfter');
        modelDialogTools.showConfirmDialog(
            deleteModelTitle,
            `${deleteModelConfirmBefore} "${name}" ${deleteModelConfirmAfter}`,
            () => {
                this.modelViewController.doObjectDelete(option.value);
                this.updateModelList();
            },
            () => {
                // console.log('modelview panel tools delete canceled', option);
            }
        );
    }

    private modelItemOnRemove(option: ListItemOption) {
        console.log('modelItemOnRemove--事件-----',option)
        this.marbleModelList = this.marbleModelList.filter(model => model.worldid !== option.value);
        this.updateModelList();
    }

    private modelItemOnLoad(option: ListItemOption) {
        this.visionaryScene.loadModel(option.modelUrl as string, { displayName: option.text });
        this.marbleModelList = this.marbleModelList.filter(model => model.worldid !== option.value);
        this.updateModelList();
    }
    private modelItemOnTextClick(option: ListItemOption) {
        this.currentSelectedId = option.value;
        this.modelViewController.doObjectSelect(option.value);
        this.notifyModelSelect(option.value);
        this.updateModelList();
    }

    private setupModelViewEventHandlers() {
        this.cameraListElement.onAdd((params: any) => {
            this.showAddMenu(params, true, false);
        });
        this.cameraListElement.onDelete((option: ListItemOption) => {
            this.cameraItemOnDelete(option);
        });
        this.cameraListElement.onHide((option: ListItemOption) => {
            this.modelViewController.updateModelVisibility(option.value, false);
            this.updateModelList();
        });
        this.cameraListElement.onShow((option: ListItemOption) => {
            this.modelViewController.updateModelVisibility(option.value, true);
            this.updateModelList();
        });
        this.cameraListElement.onTextClick((option: ListItemOption) => { // 需要更新scene中选中的对象
            this.cameraItemOnTextClick(option);
        });

        this.modelListElement.onAdd((params: any) => {
            this.showAddMenu(params, false, true);
        });

        this.modelListElement.onDelete((option: ListItemOption) => {
            this.modelItemOnDelete(option);
        });
        this.modelListElement.onLoad((option: ListItemOption) => {
            this.modelItemOnLoad(option);
        });
        this.modelListElement.onRemove((option: ListItemOption) => {
            this.modelItemOnRemove(option);
        });
        this.modelListElement.onHide((option: ListItemOption) => {
            this.modelViewController.updateModelVisibility(option.value, false);
            this.updateModelList();
        });
        this.modelListElement.onShow((option: ListItemOption) => {
            this.modelViewController.updateModelVisibility(option.value, true);
            this.updateModelList();
        });
        // this.modelListElement.onCopy((option: ListItemOption) => {
        //     this.modelViewController.doObjectCopy(option.value);
        //     this.updateModelList();
        // });
        this.modelListElement.onTextClick((option: ListItemOption) => { // 需要更新scene中选中的对象
            this.modelItemOnTextClick(option);
        });

        if (this.modelListContainer) {
            const dragActiveClass = 'model-list-drag-over';

            const handleDragEnter = (event: DragEvent) => {
                event.preventDefault();
                event.stopPropagation();
                this.dragCounter += 1;
                this.modelListContainer.classList.add(dragActiveClass);
            };

            const handleDragOver = (event: DragEvent) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = 'copy';
                }
            };

            const handleDragLeave = (event: DragEvent) => {
                event.preventDefault();
                event.stopPropagation();
                this.dragCounter = Math.max(0, this.dragCounter - 1);
                if (this.dragCounter === 0) {
                    this.modelListContainer.classList.remove(dragActiveClass);
                }
            };

            const handleDrop = (event: DragEvent) => {
                event.preventDefault();
                event.stopPropagation();

                const files = event.dataTransfer?.files;
                if (files && files.length > 0) {
                    const droppedFiles = Array.from(files);
                    const { validFiles, invalidFiles } = this.partitionFilesByType(droppedFiles);

                    if (invalidFiles.length > 0) {
                        invalidFiles.forEach(file => {
                            console.warn('模型列表拖拽文件类型不支持:', file.name, '允许的扩展名:', this.allowedDragExtensions);
                        });
                    }

                    if (validFiles.length > 0) {
                        this.fileController.loadModels(validFiles);
                    } else {
                        modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.noValidFiles'));
                        setTimeout(() => {
                            modelDialogTools.closeLoadingInfoModal();
                        }, 1000 * 5);
                    }
                } else {
                    console.log('模型列表拖拽事件触发，但未检测到有效文件');
                }

                this.dragCounter = 0;
                this.modelListContainer.classList.remove(dragActiveClass);
            };

            const handleDragEnd = (event: DragEvent) => {
                event.preventDefault();
                event.stopPropagation();
                this.dragCounter = 0;
                this.modelListContainer.classList.remove(dragActiveClass);
            };

            this.dragHandlers = {
                dragenter: handleDragEnter,
                dragover: handleDragOver,
                dragleave: handleDragLeave,
                drop: handleDrop,
                dragend: handleDragEnd,
            };

            this.dragCounter = 0;

            this.modelListContainer.addEventListener('dragenter', handleDragEnter);
            this.modelListContainer.addEventListener('dragover', handleDragOver);
            this.modelListContainer.addEventListener('dragleave', handleDragLeave);
            this.modelListContainer.addEventListener('drop', handleDrop);
            this.modelListContainer.addEventListener('dragend', handleDragEnd);
        }

        this.modelSelectChangeHandler = (event: Event) => this.onModelSelectChange(event);
        window.addEventListener('event.modelSelectChange', this.modelSelectChangeHandler);

        this.clearObjectSelectHandler = (event: Event) => this.onClearObjectSelect(event);
        window.addEventListener('event.clearObjectSelect', this.clearObjectSelectHandler);

        this.marbleModelResponseHandler = (event: Event) => this.onMarbleModelResponse(event);
        window.addEventListener('event.marbleModelResponse', this.marbleModelResponseHandler);
    }
    private onMarbleModelResponse(event: Event) {
        const detail = (event as CustomEvent).detail;
        console.log('onMarbleModelResponse--事件-----',detail)
        if (detail.status == 'success') { // 成功，更新marbleModelList状态
            const marbleModel = this.marbleModelList.find(model => model.worldid == detail.operationId);
            if (marbleModel) {
                marbleModel.status = 'success';
                marbleModel.worldid = detail.worldid;
                marbleModel.modelUrl = detail.modelUrl;
            }
        } else if (detail.status == 'create') {
            this.marbleModelList.push({
                worldid: detail.tempId,
                status: 'create',
                modelUrl: '',
                name: detail.name
            });
            modelDialogTools.closeMarbleImportDialog();
        } else if (detail.status == 'generating') {
            const marbleModel = this.marbleModelList.find(model => model.worldid == detail.tempId);
            if (marbleModel) {
                marbleModel.status = 'generating';
                marbleModel.worldid = detail.worldid;
            }
        } else {
            const marbleModel = this.marbleModelList.find(model => model.worldid == detail.worldid);
            console.log('fault-------',marbleModel,this.marbleModelList)
            if (marbleModel) {
                marbleModel.status = 'failed';
            }else{
                const marbleTempModel=this.marbleModelList.find(model => model.worldid == detail.tempId);
                if (marbleTempModel) {
                    marbleTempModel.status = 'failed';
                }
            }
        }
        this.updateModelList();
    }
    private onClearObjectSelect(event: Event) {
        const detail = (event as CustomEvent).detail;
        if (detail.sceneid == this.visionaryScene.getId()) {
            this.currentSelectedId = '';
            this.updateModelList();
        }
    }
    notifyModelSelect(id: string) {
        globalManager.setCurrentSelected(this.modelViewController.sceneid, id);
    }
    updateModelList() {
        let modelList = this.fileController.getModelList();
        let modelListOptions: ListItemOption[] = [], cameraListOptions: ListItemOption[] = [], marbleModelListOptions: ListItemOption[] = [];
        modelList.forEach(model => {
            if (model.type === 'recordingCamera') {
                cameraListOptions.push({
                    value: model.id,
                    text: model.name,
                    img: '',
                    hidden: !model.object.visible,
                    selected: model.id === this.currentSelectedId
                });
            } else {
                modelListOptions.push({
                    worldid: model.id,
                    value: model.id,
                    text: model.name,
                    img: '',
                    hidden: !model.object.visible,
                    selected: model.id === this.currentSelectedId,
                    isPlyHeatmap: model.isPlyHeatmap || false
                });
            }
        });
        this.marbleModelList.forEach(model => {
            modelListOptions.push({
                value: model.worldid,
                text: model.name,
                img: '',
                hidden: false,
                isMarbleModel: true,
                status: model.status,
                selected: model.worldid === this.currentSelectedId,
                modelUrl: model.modelUrl
            });
        });
        this.cameraListElement.setOptions(cameraListOptions as ListItemOption[]);
        this.modelListElement.setOptions(modelListOptions as ListItemOption[]);
    }

    onModelSelectChange(event: Event) {
        const detail = (event as CustomEvent).detail;

        if (detail.sceneid != this.modelViewController.sceneid) {
            this.clearObjectSelect();
        } else {
            const objectId = detail.objectid;
            if (!objectId) {
                this.clearObjectSelect();
            } else {
                this.currentSelectedId = objectId;
                this.updateModelList();
            }
        }
    }

    toogleExpand() {
        this.isExpanded = !this.isExpanded;
        if (this.isExpanded) {
            this.container?.classList.add('modelview-container-expanded');
        } else {
            this.container?.classList.remove('modelview-container-expanded');
        }
    }

    getFileController(): FileController {
        return this.fileController;
    }
    clearObjectSelect() {
        this.currentSelectedId = '';
        this.modelViewController.clearObjectSelect();
        this.updateModelList();
    }
    /**
     * 添加模型选择操作监听器
     * @param callback 回调函数
     */
    onModelSelect(callback: (id: string, sceneid: string) => void): void {
        this.onModelSelectCallbacks.push(callback);
    }

    /**
     * 移除模型选择操作监听器
     * @param callback 要移除的回调函数
     */
    offModelSelect(callback: (id: string, sceneid: string) => void): void {
        const index = this.onModelSelectCallbacks.indexOf(callback);
        if (index > -1) {
            this.onModelSelectCallbacks.splice(index, 1);
        }
    }

    resetRecordingCameraNumber() {
        this.camerasCount = 0;
    }

    /**
     * 显示添加菜单（载入模型/录制相机）
     */
    private showAddMenu(params: any, showCamera: boolean, showModel: boolean): void {
        let addButton: HTMLElement;
        // 获取加号按钮的位置
        if (params) {
            addButton = params;
        } else {
            addButton = this.modelListElement.getContainer().querySelector('.selection-list-action-btn.icon-PlusOutlined') as HTMLElement;
        }
        if (!addButton) return;
        // 创建下拉菜单
        const menu = document.createElement('div');
        menu.className = 'add-model-dropdown-menu dropdown-menu show';
        const currentLang = i18next.language?.toLowerCase();
        // if (currentLang?.startsWith('en')) {
        //     menu.style.setProperty('width', '12rem', 'important');
        //     menu.style.setProperty('min-width', '12rem', 'important');
        //     menu.style.setProperty('max-width', '12rem', 'important');
        // }

        if (showCamera) {

            // 菜单项2：录制相机
            const addCameraItem = document.createElement('button');
            addCameraItem.className = 'dropdown-item';
            addCameraItem.innerHTML = `<div class="dropdown-item-content d-flex align-items-center"><i class="icon iconfont icon-CameraOutlined scene-open icon-sm icon-dropdown-tab-item"></i><span class="tabitem-title" data-i18n="buttons.addRecordingCamera"></span></div>`;

            // 手动设置翻译文本（因为通过innerHTML创建的DOM，data-i18n属性无法自动处理）
            const addCameraSpan = addCameraItem.querySelector('.tabitem-title[data-i18n="buttons.addRecordingCamera"]') as HTMLElement;
            if (addCameraSpan) {
                addCameraSpan.textContent = getI18nText('buttons.addRecordingCamera');
            }
            addCameraItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.addRecordingCamera();
                this.hideAddMenu(menu);
            });
            menu.appendChild(addCameraItem);
        }
        if (showModel) {

            // 菜单项1：载入模型 - 本地模型
            const loadModelItem = document.createElement('button');
            loadModelItem.className = 'dropdown-item';
            loadModelItem.innerHTML = `<div class="dropdown-item-content d-flex align-items-center"><i class="icon iconfont icon-UploadingOutlined scene-open icon-sm icon-dropdown-tab-item"></i><span class="tabitem-title" data-i18n="buttons.loadModel"></span></div>`;

            // 手动设置翻译文本（因为通过innerHTML创建的DOM，data-i18n属性无法自动处理）
            const loadModelSpan = loadModelItem.querySelector('.tabitem-title[data-i18n="buttons.loadModel"]') as HTMLElement;
            if (loadModelSpan) {
                loadModelSpan.textContent = getI18nText('buttons.loadModel');
            }

            loadModelItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.fileController.openModelFileDialog();
                this.hideAddMenu(menu);
            });
            menu.appendChild(loadModelItem);


            // 菜单项3：载入Marble模型
            const loadMarbleModelItem = document.createElement('button');
            loadMarbleModelItem.className = 'dropdown-item';
            loadMarbleModelItem.innerHTML = `<div class="dropdown-item-content d-flex align-items-center"><i class="icon iconfont icon-MarbleFilled scene-open icon-sm icon-dropdown-tab-item"></i><span class="tabitem-title" data-i18n="buttons.loadMarbleModel"></span></div>`;
            const loadMarbleModelSpan = loadMarbleModelItem.querySelector('.tabitem-title[data-i18n="buttons.loadMarbleModel"]') as HTMLElement;
            if (loadMarbleModelSpan) {
                loadMarbleModelSpan.textContent = getI18nText('buttons.loadMarbleModel');
            }
            loadMarbleModelItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.fileController.openMarbleModelFileDialog();
                this.hideAddMenu(menu);
            });
            menu.appendChild(loadMarbleModelItem);

            // 菜单项4：浏览生成历史
            const viewMarbleHistoryItem = document.createElement('button');
            viewMarbleHistoryItem.className = 'dropdown-item';
            viewMarbleHistoryItem.innerHTML = `<div class="dropdown-item-content d-flex align-items-center"><i class="icon iconfont icon-HistoryOutlined scene-open icon-sm icon-dropdown-tab-item"></i><span class="tabitem-title" data-i18n="buttons.viewMarbleHistory"></span></div>`;
            const viewMarbleHistorySpan = viewMarbleHistoryItem.querySelector('.tabitem-title[data-i18n="buttons.viewMarbleHistory"]') as HTMLElement;
            if (viewMarbleHistorySpan) {
                viewMarbleHistorySpan.textContent = getI18nText('buttons.viewMarbleHistory');
            }
            viewMarbleHistoryItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.fileController.openMarbleModelListDialog();
                this.hideAddMenu(menu);
            });
            menu.appendChild(viewMarbleHistoryItem);
        }
        // 添加到body并定位
        document.body.appendChild(menu);

        // 计算菜单位置
        const rect = addButton.getBoundingClientRect();
        menu.style.left = `${rect.left}px`;
        menu.style.top = `${rect.bottom + 4}px`;

        // 点击外部关闭菜单
        const clickOutsideHandler = (e: MouseEvent) => {
            if (!menu.contains(e.target as Node) && e.target !== addButton) {
                this.hideAddMenu(menu);
                document.removeEventListener('click', clickOutsideHandler);
            }
        };
        // 延迟添加事件监听，避免立即触发
        setTimeout(() => {
            document.addEventListener('click', clickOutsideHandler);
        }, 0);
    }

    /**
     * 隐藏添加菜单
     */
    private hideAddMenu(menu: HTMLElement): void {
        if (menu && menu.parentNode) {
            menu.parentNode.removeChild(menu);
        }
    }

    /**
     * 添加录制相机
     */
    private addRecordingCamera(): void {
        const scene = this.visionaryScene;
        if (!scene) {
            console.error('[ModelViewPanelTools] 无法获取场景实例');
            return;
        }

        const camera = scene.getCamera();
        if (!camera) {
            console.error('[ModelViewPanelTools] 无法获取场景相机');
            return;
        }

        // 创建录制相机（复制主相机的状态）
        const newRecordingCameraName = getI18nText('label.newRecordingCameraName');
        const cameraName = `${newRecordingCameraName}_${this.camerasCount + 1}`;
        const recordingCamera = new RecordingCamera(scene.getId(), globalConfig.recordingCameraParams.width, globalConfig.recordingCameraParams.height, globalConfig.recordingCameraParams.fov, true, cameraName, false);
        recordingCamera.setPosition(camera.position.x, camera.position.y, camera.position.z);
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        const target = new THREE.Vector3().copy(camera.position).add(direction);
        recordingCamera.lookAt(target.x, target.y, target.z);

        // 将RecordingCamera添加到场景管理列表
        const sceneObject = recordingCamera.getSceneObject();
        if (sceneObject && scene.getScene()) {
            scene.getScene()!.add(sceneObject);

            const visionaryObject = new VisionaryObject(
                sceneObject.uuid,
                cameraName,
                'recordingCamera',
                sceneObject,
                null as any,
                ''
            );
            scene.handleObjectsPush(visionaryObject);
            this.camerasCount++;
            this.updateModelList();
        }
    }

    /**
     * 销毁模型视图面板并释放所有资源
     */
    destroy() {
        console.log('destroying ModelViewPanelTools:', this.panelId);

        // 1. 移除窗口事件监听器
        if (this.modelSelectChangeHandler) {
            window.removeEventListener('event.modelSelectChange', this.modelSelectChangeHandler);
        }
        if (this.clearObjectSelectHandler) {
            window.removeEventListener('event.clearObjectSelect', this.clearObjectSelectHandler);
        }
        if (this.marbleModelResponseHandler) {
            window.removeEventListener('event.marbleModelResponse', this.marbleModelResponseHandler);
        }

        // 2. 清理回调数组
        this.onModelSelectCallbacks = [];

        // 3. 销毁模型列表元素
        if (this.modelListElement && typeof this.modelListElement.destroy === 'function') {
            try {
                this.modelListElement.destroy();
            } catch (error) {
                console.warn('清理 modelListElement 失败:', error);
            }
        }

        if (this.modelListContainer && this.dragHandlers) {
            this.modelListContainer.removeEventListener('dragenter', this.dragHandlers.dragenter);
            this.modelListContainer.removeEventListener('dragover', this.dragHandlers.dragover);
            this.modelListContainer.removeEventListener('dragleave', this.dragHandlers.dragleave);
            this.modelListContainer.removeEventListener('drop', this.dragHandlers.drop);
            this.modelListContainer.removeEventListener('dragend', this.dragHandlers.dragend);
            this.modelListContainer.classList.remove('model-list-drag-over');
        }

        // 4. 清理引用
        this.modelListElement = null!;
        this.modelListContainer = null!;
        this.dragHandlers = null;
        this.dragCounter = 0;
        this.fileController = null!;
        this.modelViewController = null!;

        // 5. 调用基类的销毁方法
        super.destroy();

        console.log('ModelViewPanelTools destroyed:', this.panelId);
    }

    private partitionFilesByType(files: File[]): { validFiles: File[]; invalidFiles: File[] } {
        const validFiles: File[] = [];
        const invalidFiles: File[] = [];

        files.forEach(file => {
            const ext = this.getFileExtension(file.name);
            if (ext && this.allowedDragExtensions.includes(ext.toLowerCase())) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file);
            }
        });

        return { validFiles, invalidFiles };
    }

    private getFileExtension(filename: string): string {
        const normalized = filename.trim();
        const index = normalized.lastIndexOf('.');
        if (index === -1 || index === normalized.length - 1) {
            return '';
        }
        return normalized.substring(index + 1).toLowerCase();
    }
}

